import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import { ExpandLessRounded, ExpandMoreRounded, RefreshRounded, InfoOutlined as InfoOutlinedIcon, FilterAltRounded } from "@mui/icons-material";

import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { BUY_QTY_SHEET_API } from "../data";
import {
	tone,
	priceBandTones,
	ink,
	muted,
	line,
	panelBg,
	card,
	eyebrow,
	colHead,
	nf,
	KpiTile,
	MiniBars,
	DimensionToggle,
	ScopeChips,
	ANALYTICS_SUPPORTED_FILTERS,
	isActiveFilterValue,
} from "components/PimDashboard/DashboardComponents/shared/analyticsUi";

const QTY_BANDS = [
	{ key: "1-50", label: "1–50", color: priceBandTones[0] },
	{ key: "51-200", label: "51–200", color: priceBandTones[1] },
	{ key: "201-500", label: "201–500", color: priceBandTones[2] },
	{ key: "501-2k", label: "501–2k", color: priceBandTones[3] },
	{ key: "2k+", label: "2k+", color: priceBandTones[4] },
];

const DIMENSIONS = [
	{ key: "brand", label: "Brand" },
	{ key: "season", label: "Season" },
];

const BUY_QTY_ANALYTICS_FILTERS = new Set([
	...ANALYTICS_SUPPORTED_FILTERS,
	"buy_qty_status",
	"buy_created_by",
	"buy_updated_by",
	"buy_published_by",
]);

const splitBuyQtyFilters = (filter = {}) => {
	const active = Object.keys(filter || {}).filter((k) => isActiveFilterValue(filter[k]));
	return {
		applied: active.filter((k) => BUY_QTY_ANALYTICS_FILTERS.has(k)),
		ignored: active.filter((k) => !BUY_QTY_ANALYTICS_FILTERS.has(k)),
	};
};

const MID_ROW_H = { xs: "auto", md: 320 };

const BandSwatches = ({ bands }) => {
	const byKey = Object.fromEntries((bands || []).map((b) => [b.band, Number(b.total || 0)]));
	const tip = QTY_BANDS.map((b) => `${b.label}: ${nf(byKey[b.key] || 0)}`).join(" · ");
	return (
		<Tooltip arrow title={tip || "No qty colors"}>
			<Stack direction="row" spacing={0.35} sx={{ cursor: "help" }}>
				{QTY_BANDS.map((b) => {
					const n = byKey[b.key] || 0;
					return (
						<Box
							key={b.key}
							sx={{
								width: 9,
								height: 9,
								borderRadius: 0.4,
								background: n ? b.color : "#EEF0F3",
								outline: n ? "none" : `1px solid ${line}`,
							}}
						/>
					);
				})}
			</Stack>
		</Tooltip>
	);
};

const CompareRow = ({ row }) => (
	<Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.95, borderTop: `1px solid ${line}` }}>
		<Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: ink }} noWrap title={row.key}>
			{row.key}
		</Typography>
		<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>{nf(row.total)}</Typography>
		<Typography sx={{ width: 36, fontSize: 11.5, color: muted, textAlign: "right", flexShrink: 0 }}>
			{row.pricedRate}%
		</Typography>
		<Box sx={{ width: 56, flexShrink: 0, display: "flex", justifyContent: "center" }}>
			<BandSwatches bands={row.bands} />
		</Box>
		<Typography sx={{ width: 52, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
			{nf(row.medianQty ?? row.medianMrp)}
		</Typography>
		<Typography
			sx={{
				width: 28,
				fontSize: 11.5,
				textAlign: "right",
				flexShrink: 0,
				color: row.blocked ? "#B4696B" : muted,
			}}>
			{nf(row.blocked)}
		</Typography>
	</Stack>
);

const QtyBandRing = ({ bands }) => {
	const raw = QTY_BANDS.map((b) => {
		const match = (bands || []).find((row) => row.band === b.key || row.band === b.label);
		return {
			...b,
			total: Number(match?.total || 0),
			optionCount: Number(match?.optionCount || 0),
		};
	});
	const styleTotal = raw.reduce((s, i) => s + i.total, 0);
	const optionTotal = raw.reduce((s, i) => s + i.optionCount, 0);
	const pct = (part, whole) => (whole ? Number(((100 * part) / whole).toFixed(1)) : 0);
	const items = raw.map((i) => ({
		...i,
		rate: pct(i.total, styleTotal),
		optionRate: pct(i.optionCount, optionTotal),
	}));
	let cursor = 0;
	const gradient =
		styleTotal > 0
			? items
					.filter((i) => i.total > 0)
					.map((i) => {
						const start = cursor;
						cursor += (100 * i.total) / styleTotal;
						return `${i.color} ${start}% ${cursor}%`;
					})
					.join(", ")
			: `${tone.neutral} 0% 100%`;

	return (
		<Box
			sx={{
				...card,
				width: { xs: "100%", md: 420 },
				height: MID_ROW_H,
				flexShrink: 0,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}>
			<Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
				<Typography sx={{ ...eyebrow, flex: 1 }}>Qty bands</Typography>
				<Tooltip
					arrow
					title="Colors with total qty > 0 by qty band. Style % = share of styles that have at least one such color. Colors = option rows; color % = share of all colors with qty.">
					<InfoOutlinedIcon sx={{ fontSize: 14, color: muted, cursor: "help" }} />
				</Tooltip>
			</Stack>

			<Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1.5, flex: 1, minHeight: 0 }}>
				<Box
					sx={{
						width: 88,
						height: 88,
						borderRadius: "50%",
						background: `conic-gradient(${gradient})`,
						position: "relative",
						boxShadow: `inset 0 0 0 1px ${line}`,
						flexShrink: 0,
					}}>
					<Box
						sx={{
							position: "absolute",
							inset: 20,
							borderRadius: "50%",
							background: "#fff",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<Typography sx={{ fontSize: 15, fontWeight: 600, color: ink, lineHeight: 1 }}>{nf(styleTotal)}</Typography>
						<Typography sx={{ fontSize: 8.5, color: muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>
							styles
						</Typography>
						<Typography sx={{ fontSize: 8.5, color: muted, mt: 0.15 }}>{nf(optionTotal)} opts</Typography>
					</Box>
				</Box>

				<Stack spacing={0.55} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto" }}>
					<Stack direction="row" spacing={0.75}>
						<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>Band</Typography>
						<Typography sx={{ ...colHead, width: 58, textAlign: "right", flexShrink: 0 }}>Styles</Typography>
						<Typography sx={{ ...colHead, width: 58, textAlign: "right", flexShrink: 0 }}>Opts</Typography>
					</Stack>
					{items.map((i) => (
						<Tooltip
							key={i.key}
							arrow
							title={`${i.label}: ${nf(i.total)} styles (${i.rate}%) · ${nf(i.optionCount)} colors (${i.optionRate}%)`}>
							<Stack direction="row" alignItems="center" spacing={0.75} sx={{ cursor: "help" }}>
								<Box sx={{ width: 8, height: 8, borderRadius: 0.4, background: i.color, flexShrink: 0 }} />
								<Typography sx={{ flex: 1, fontSize: 11, color: muted, minWidth: 0 }} noWrap>
									{i.label}
								</Typography>
								<Box sx={{ width: 58, flexShrink: 0, textAlign: "right" }}>
									<Typography sx={{ fontSize: 12, fontWeight: 600, color: i.total ? ink : muted, lineHeight: 1.1 }}>
										{nf(i.total)}
									</Typography>
									<Typography sx={{ fontSize: 9.5, color: muted, lineHeight: 1.1 }}>{i.rate}%</Typography>
								</Box>
								<Box sx={{ width: 58, flexShrink: 0, textAlign: "right" }}>
									<Typography
										sx={{ fontSize: 12, fontWeight: 600, color: i.optionCount ? ink : muted, lineHeight: 1.1 }}>
										{nf(i.optionCount)}
									</Typography>
									<Typography sx={{ fontSize: 9.5, color: muted, lineHeight: 1.1 }}>{i.optionRate}%</Typography>
								</Box>
							</Stack>
						</Tooltip>
					))}
				</Stack>
			</Stack>
		</Box>
	);
};

const ZeroQtyAlert = ({ alert }) => {
	if (!alert?.publishedAtZero) return null;
	const seasons = alert.bySeason || [];
	return (
		<Box
			sx={{
				border: "1px solid #E8C9C9",
				background: "#FDF6F6",
				borderRadius: 2.5,
				p: 2,
			}}>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
				<Box sx={{ flex: "0 1 280px", minWidth: 0 }}>
					<Typography sx={{ ...eyebrow, color: "#8F4A4C" }}>Data issue · published at qty 0</Typography>
					<Typography sx={{ fontSize: 22, fontWeight: 500, color: "#8F4A4C", mt: 0.5, lineHeight: 1.2 }}>
						{nf(alert.publishedAtZero)}
					</Typography>
					<Typography sx={{ fontSize: 12, color: "#8F4A4C", mt: 0.5, opacity: 0.85 }}>
						Published buy qty colors with total qty = 0.
						{alert.pendingAtZero ? ` ${nf(alert.pendingAtZero)} BUYING unpublished also at 0.` : null}
					</Typography>
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" spacing={1.5} sx={{ pb: 0.5 }}>
						<Typography sx={{ ...colHead, width: 72, flexShrink: 0 }}>Season</Typography>
						<Typography sx={{ ...colHead, width: 72, textAlign: "right", flexShrink: 0 }}>Published</Typography>
						<Typography sx={{ ...colHead, width: 64, textAlign: "right", flexShrink: 0 }}>At 0</Typography>
						<Typography sx={{ ...colHead, width: 48, textAlign: "right", flexShrink: 0 }}>%</Typography>
					</Stack>
					{(seasons.length ? seasons : []).slice(0, 8).map((r) => (
						<Stack key={r.season} direction="row" spacing={1.5} sx={{ py: 0.45 }}>
							<Typography sx={{ width: 72, fontSize: 12, color: ink, flexShrink: 0, fontWeight: 600 }} noWrap>
								{r.season}
							</Typography>
							<Typography sx={{ width: 72, fontSize: 12, color: ink, textAlign: "right", flexShrink: 0 }}>
								{nf(r.published)}
							</Typography>
							<Typography sx={{ width: 64, fontSize: 12, color: "#8F4A4C", textAlign: "right", flexShrink: 0 }}>
								{nf(r.atZero)}
							</Typography>
							<Typography sx={{ width: 48, fontSize: 12, color: muted, textAlign: "right", flexShrink: 0 }}>
								{r.atZeroRate}%
							</Typography>
						</Stack>
					))}
					{!seasons.length ? (
						<Typography sx={{ fontSize: 12, color: muted, py: 1 }}>No season breakdown.</Typography>
					) : null}
				</Box>
			</Stack>
		</Box>
	);
};

const ScopeSelect = ({ label, options, value, onChange, disabled, compact = false }) => (
	<Select
		multiple
		displayEmpty
		size="small"
		disabled={disabled || !options.length}
		value={value}
		onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
		renderValue={(selected) =>
			!selected.length
				? compact
					? label
					: `All ${label.toLowerCase()}s`
				: selected.length === 1
					? selected[0]
					: `${label}: ${selected.length}`
		}
		MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
		sx={{
			minWidth: compact ? 88 : 132,
			maxWidth: compact ? 110 : undefined,
			fontSize: compact ? 11 : 12,
			background: "#fff",
			"& .MuiOutlinedInput-notchedOutline": { borderColor: line },
			"& .MuiSelect-select": { py: compact ? 0.35 : 0.6, color: value.length ? ink : muted },
		}}>
		{options.map((opt) => (
			<MenuItem key={opt} value={opt} dense>
				<Checkbox size="small" checked={value.indexOf(opt) > -1} sx={{ py: 0.25 }} />
				<ListItemText primaryTypographyProps={{ fontSize: compact ? 11.5 : 12.5 }} primary={opt} />
			</MenuItem>
		))}
	</Select>
);

const EMPTY_FILTER = Object.freeze({});

const BuyQtyAnalytics = ({ filter = EMPTY_FILTER, search = "", onMoreFilters, activeFilterCount = 0 }) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(true);
	const [dimension, setDimension] = useState("brand");
	const [scopeOptions, setScopeOptions] = useState({ season: [], brand: [], division: [] });
	const scopeOptionsReady = useRef(false);
	const [catScope, setCatScope] = useState({ brand: [], division: [], season: [] });
	const [catRows, setCatRows] = useState(null);
	const [catLoading, setCatLoading] = useState(false);

	const effectiveFilter = useMemo(() => ({ ...(filter || EMPTY_FILTER) }), [filter]);
	const filterKey = useMemo(() => JSON.stringify(effectiveFilter || {}), [effectiveFilter]);
	const panelFilterEmpty = useMemo(() => {
		const keys = Object.keys(effectiveFilter || {}).filter((k) => {
			const v = effectiveFilter[k];
			if (v == null || v === "") return false;
			if (Array.isArray(v)) return v.length > 0;
			return true;
		});
		return keys.length === 0 && !String(search || "").trim();
	}, [effectiveFilter, search]);

	const isCatScoped = Boolean(catScope.brand.length || catScope.division.length || catScope.season.length);

	const catFilter = useMemo(() => {
		const next = { ...effectiveFilter };
		for (const key of ["brand", "division", "season"]) {
			if (catScope[key]?.length) next[key] = catScope[key];
		}
		return next;
	}, [effectiveFilter, catScope]);
	const catFilterKey = useMemo(() => JSON.stringify(catFilter || {}), [catFilter]);

	const fetchSummary = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const { data: res } = await axios.post(`${BUY_QTY_SHEET_API}/analytics/summary`, {
				filter: effectiveFilter,
				search,
			});
			const result = res?.result || null;
			setData(result);
			if (result && panelFilterEmpty && !scopeOptionsReady.current) {
				scopeOptionsReady.current = true;
				setScopeOptions({
					season: (result.groups?.season?.rows || []).map((r) => r.key),
					brand: (result.groups?.brand?.rows || []).map((r) => r.key),
					division: result.filterOptions?.division || [],
				});
			}
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterKey, search]);

	useEffect(() => {
		fetchSummary();
	}, [fetchSummary]);

	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			if (!isCatScoped) {
				setCatRows((prev) => (prev == null ? prev : null));
				setCatLoading(false);
				return;
			}
			setCatLoading(true);
			try {
				const { data: res } = await axios.post(`${BUY_QTY_SHEET_API}/analytics/summary`, {
					filter: catFilter,
					search,
				});
				if (!cancelled) setCatRows(res?.result?.qty?.byCategory || []);
			} catch {
				if (!cancelled) setCatRows([]);
			} finally {
				if (!cancelled) setCatLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCatScoped, catFilterKey, search]);

	const { applied, ignored } = useMemo(() => splitBuyQtyFilters(effectiveFilter), [effectiveFilter]);

	const group = data?.groups?.[dimension];
	const rows = useMemo(() => group?.rows || [], [group]);

	const coverage = data?.coverage;
	const readiness = data?.readiness;
	const qty = data?.qty;
	const blockedTotal = (readiness?.blockedQty || readiness?.blockedMrp || 0) + (readiness?.blockedSizes || 0);
	const categoryRows = isCatScoped ? catRows || [] : qty?.byCategory || [];

	return (
		<Box sx={{ background: panelBg, border: `1px solid ${line}`, borderRadius: 3, p: 2.5, mb: 2 }}>
			<Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: 14.5, fontWeight: 600, color: ink, flexShrink: 0 }}>Buy qty analytics</Typography>
				<Box sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
					<ScopeChips applied={applied} ignored={ignored} search={search} emptyLabel="all colors" />
				</Box>
				{typeof onMoreFilters === "function" ? (
					<Button
						size="small"
						variant="outlined"
						startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
						onClick={onMoreFilters}
						sx={{
							textTransform: "none",
							fontSize: 12,
							color: muted,
							borderColor: activeFilterCount ? ink : line,
							bgcolor: activeFilterCount ? "#fff" : "transparent",
							borderRadius: 1,
							minHeight: 30,
							fontWeight: activeFilterCount ? 600 : 500,
							flexShrink: 0,
						}}>
						More Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
					</Button>
				) : null}
				<Tooltip title="Refresh" arrow>
					<span>
						<IconButton size="small" onClick={fetchSummary} disabled={loading}>
							{loading ? (
								<CircularProgress size={14} sx={{ color: muted }} />
							) : (
								<RefreshRounded sx={{ fontSize: 17, color: muted }} />
							)}
						</IconButton>
					</span>
				</Tooltip>
				<IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Collapse" : "Expand"}>
					{open ? (
						<ExpandLessRounded sx={{ fontSize: 19, color: muted }} />
					) : (
						<ExpandMoreRounded sx={{ fontSize: 19, color: muted }} />
					)}
				</IconButton>
			</Stack>

			<Collapse in={open}>
				{error ? (
					<Typography sx={{ fontSize: 13, color: "#B4696B", mt: 2 }}>{error}</Typography>
				) : !data && loading ? (
					<Stack alignItems="center" sx={{ py: 5 }}>
						<CircularProgress size={22} sx={{ color: tone.mrpPending }} />
					</Stack>
				) : data ? (
					<Stack spacing={2} sx={{ mt: 2 }}>
						<ZeroQtyAlert alert={data.alert} />

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									width: { xs: "100%", md: "50%" },
									flexShrink: 0,
									boxSizing: "border-box",
									"& > *": { flex: "1 1 0", minWidth: 120 },
								}}>
								<KpiTile
									label="Qty coverage"
									value={`${coverage?.pricedRate ?? 0}%`}
									hint={`${nf(coverage?.withQty ?? coverage?.priced)} of ${nf(coverage?.total)} · total qty > 0`}
									accent={tone.published}
									info="Share of colors in the current scope with buy qty total > 0."
								/>
								<KpiTile
									label="Ready to publish"
									value={nf(readiness?.ready)}
									hint={`of ${nf(readiness?.pending)} BUYING unpublished`}
									accent={tone.designerPending}
									info="BUYING-stage colors that are not published, have total qty > 0, and have sizes on the style."
								/>
								<KpiTile
									label="Blocked"
									value={nf(blockedTotal)}
									hint={`${nf(readiness?.blockedQty ?? readiness?.blockedMrp)} on qty · ${nf(readiness?.blockedSizes)} on sizes`}
									accent={tone.rejected}
									info="BUYING unpublished colors that cannot publish: total qty is 0, or qty is set but the style has no sizes."
								/>
								<KpiTile
									label="Avg qty"
									value={nf(qty?.meanQty)}
									hint={qty?.medianQty != null ? `Median qty ${nf(qty.medianQty)}` : "Median qty —"}
									accent={tone.mrpPending}
									info="Over colors with total qty > 0. Avg is the arithmetic mean; Median is the middle value."
								/>
							</Stack>

							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									flex: 1,
									minWidth: 0,
									boxSizing: "border-box",
									"& > *": { flex: "1 1 0", minWidth: 140, maxWidth: 220 },
								}}>
								<KpiTile
									label="Total qty"
									value={nf(data.margin?.totalQty ?? coverage?.totalQty)}
									hint={
										data.margin?.published != null
											? `${nf(data.margin.published)} published`
											: "Published —"
									}
									accent={tone.designerPending}
									info={`Sum of buy qty across colors in scope. Published = colors with buy qty status PUBLISHED (n=${nf(data.margin?.published || 0)}).`}
								/>
							</Stack>
						</Stack>

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
							<Box
								sx={{
									...card,
									width: { xs: "100%", md: "25%" },
									minWidth: { md: 280 },
									maxWidth: { md: 360 },
									height: MID_ROW_H,
									flexShrink: 0,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0 }}>
									<Typography sx={eyebrow}>Compare by</Typography>
									<DimensionToggle
										value={dimension}
										onChange={setDimension}
										options={DIMENSIONS}
										selectedTone={tone.mrpPending}
									/>
								</Stack>

								<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, pb: 0.5, flexShrink: 0 }}>
									<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>
										{dimension === "brand" ? "Brand" : "Season"}
									</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>N</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>%</Typography>
									<Typography sx={{ ...colHead, width: 56, textAlign: "center", flexShrink: 0 }}>Bands</Typography>
									<Typography sx={{ ...colHead, width: 52, textAlign: "right", flexShrink: 0 }}>Med</Typography>
									<Typography sx={{ ...colHead, width: 28, textAlign: "right", flexShrink: 0 }}>Blk</Typography>
								</Stack>

								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									{rows.length ? (
										rows.map((row) => <CompareRow key={row.key} row={row} />)
									) : (
										<Typography sx={{ fontSize: 12, color: muted, py: 1.5 }}>No colors to compare.</Typography>
									)}
								</Box>

								<Typography sx={{ fontSize: 10.5, color: muted, mt: 1, lineHeight: 1.45, flexShrink: 0 }}>
									Hover bands for counts. Qty &gt; 0 only. Blk = BUYING unpublished with qty 0.
									{group?.truncated ? (
										<Box component="span" sx={{ color: "#8A6A2F", fontWeight: 600 }}>
											{" "}
											Top {rows.length} only.
										</Box>
									) : null}
								</Typography>
							</Box>

							<QtyBandRing bands={qty?.bands || []} />

							<Box
								sx={{
									...card,
									flex: 1,
									minWidth: { xs: "100%", md: 280 },
									height: MID_ROW_H,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={0.75}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0, mb: 0.5 }}>
									<Typography sx={{ ...eyebrow, mr: 0.5 }}>Qty by category</Typography>
									<ScopeSelect
										compact
										label="Brand"
										options={scopeOptions.brand}
										value={catScope.brand}
										onChange={(v) => setCatScope((s) => ({ ...s, brand: v }))}
										disabled={loading && !data}
									/>
									<ScopeSelect
										compact
										label="Division"
										options={scopeOptions.division}
										value={catScope.division}
										onChange={(v) => setCatScope((s) => ({ ...s, division: v }))}
										disabled={loading && !data}
									/>
									<ScopeSelect
										compact
										label="Season"
										options={scopeOptions.season}
										value={catScope.season}
										onChange={(v) => setCatScope((s) => ({ ...s, season: v }))}
										disabled={loading && !data}
									/>
									{isCatScoped ? (
										<Button
											size="small"
											onClick={() => setCatScope({ brand: [], division: [], season: [] })}
											sx={{ fontSize: 10.5, textTransform: "none", color: muted, minWidth: 0, px: 0.75 }}>
											Clear
										</Button>
									) : null}
									{catLoading ? <CircularProgress size={12} sx={{ color: muted }} /> : null}
								</Stack>
								<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, pb: 0.5, flexShrink: 0 }}>
									<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>Category</Typography>
									<Typography sx={{ ...colHead, width: 32, textAlign: "right", flexShrink: 0 }}>N</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>Opts</Typography>
									<Typography sx={{ ...colHead, width: 64, textAlign: "right", flexShrink: 0 }}>Qty(AVG)</Typography>
									<Typography sx={{ ...colHead, width: 52, textAlign: "right", flexShrink: 0 }}>Pub</Typography>
									<Typography sx={{ ...colHead, width: 72, textAlign: "right", flexShrink: 0 }}>Range</Typography>
									<Typography sx={{ ...colHead, width: 40, textAlign: "right", flexShrink: 0 }}>Pub%</Typography>
								</Stack>
								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									{categoryRows.length ? (
										categoryRows.map((r) => (
											<Stack
												key={r.category}
												direction="row"
												alignItems="center"
												spacing={0.75}
												sx={{ py: 0.7, borderTop: `1px solid ${line}` }}>
												<Typography
													sx={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: ink }}
													noWrap
													title={r.category}>
													{r.category}
												</Typography>
												<Typography sx={{ width: 32, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{nf(r.styleCount ?? r.priced)}
												</Typography>
												<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{nf(r.optionCount)}
												</Typography>
												<Typography sx={{ width: 64, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{nf(r.meanQty ?? r.meanMrp)}
												</Typography>
												<Typography sx={{ width: 52, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{nf(r.published ?? r.meanCogs)}
												</Typography>
												<Typography sx={{ width: 72, fontSize: 10.5, color: muted, textAlign: "right", flexShrink: 0 }} noWrap>
													{(r.minQty ?? r.minMrp) != null && (r.maxQty ?? r.maxMrp) != null
														? `${nf(r.minQty ?? r.minMrp)}–${nf(r.maxQty ?? r.maxMrp)}`
														: "—"}
												</Typography>
												<Tooltip
													arrow
													title={
														r.medianMultiple != null
															? `Published share of colors with qty in this category (${nf(r.multipleSample)} colors)`
															: "No colors with qty in this category"
													}>
													<Typography
														sx={{ width: 40, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
														{r.medianMultiple != null ? `${Math.round(Number(r.medianMultiple) * 100)}%` : "—"}
													</Typography>
												</Tooltip>
											</Stack>
										))
									) : (
										<Typography sx={{ fontSize: 12, color: muted, py: 1.5 }}>
											{catLoading ? "Loading…" : "No categories with qty in this scope."}
										</Typography>
									)}
								</Box>
								<Typography sx={{ fontSize: 9.5, color: muted, mt: 1, lineHeight: 1.35, flexShrink: 0 }}>
									Qty &gt; 0 only. Qty(AVG) = mean total qty. Pub = published colors. Pub% = published share.
									Card filters apply here only.
								</Typography>
							</Box>

							<Box
								sx={{
									...card,
									width: { xs: "100%", md: 168 },
									height: MID_ROW_H,
									flexShrink: 0,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Typography sx={{ ...eyebrow, flexShrink: 0 }}>BUYING ageing</Typography>
								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									<MiniBars
										dense
										rows={readiness?.aging || []}
										labelKey="bucket"
										valueKey="total"
										color={tone.mrpPending}
									/>
								</Box>
								<Typography sx={{ fontSize: 9.5, color: muted, mt: 1, lineHeight: 1.35, flexShrink: 0 }}>
									BUYING unpublished colors. Uses buy qty created date when present; otherwise style created date.
								</Typography>
							</Box>
						</Stack>
					</Stack>
				) : null}
			</Collapse>
		</Box>
	);
};

export default BuyQtyAnalytics;
